import type { DiaSS, DiaSSHermandadDetalles, MapaCalibPunto, RecorridoPunto, RecorridoSolicitud } from '@/lib/types';

/**
 * Orden litúrgico canónico de los días de la Semana Santa.
 *
 * El campo `orden` de `dias_ss` se ha visto duplicado (el Domingo de
 * Resurrección con el mismo `orden` que el Jueves Santo, y por eso salía antes
 * de la Madrugá), así que **manda el nombre del día** y el `orden` solo desempata
 * los días que no reconocemos.
 */
export const ORDEN_DIAS = [
  'Miércoles de Pasión',
  'Jueves de Pasión',
  'Viernes de Dolores',
  'Sábado de Pasión',
  'Domingo de Ramos',
  'Lunes Santo',
  'Martes Santo',
  'Miércoles Santo',
  'Jueves Santo',
  'Madrugá',
  'Viernes Santo',
  'Sábado Santo',
  'Domingo de Resurrección',
];

/**
 * Comparación tolerante: sin tildes, sin mayúsculas, solo letras y colapsando
 * letras repetidas, para que casen tanto "Madruga" como el "Resurección" que hay
 * escrito en el programa (con una sola erre).
 */
const DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g');
export const normalizeDia = (dia: string) =>
  (dia || '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/(.)\1+/g, '$1');

const ORDEN_POR_NOMBRE = new Map(ORDEN_DIAS.map((d, i) => [normalizeDia(d), i]));

export const posicionDelDia = (dia: DiaSS): number => {
  const conocido = ORDEN_POR_NOMBRE.get(normalizeDia(dia.nombredia || ''));
  // Los días que no reconocemos van al final, entre ellos por su `orden`.
  return conocido !== undefined ? conocido : ORDEN_DIAS.length + (dia.orden ?? 0);
};

/** Días del programa en orden litúrgico (vísperas primero, Resurrección al final). */
export const ordenarDiasSS = (dias: DiaSS[]): DiaSS[] =>
  [...dias].sort((a, b) => posicionDelDia(a) - posicionDelDia(b));

/**
 * Los ids de hermandad de un día del programa: `hermandades` puede ser un array
 * de ids sueltos (formato antiguo) o de objetos con detalles.
 */
export const idsDelDia = (dia: DiaSS): string[] =>
  (dia.hermandades || [])
    .map((h) => (typeof h === 'string' ? h : h?.hermandadId))
    .filter((id): id is string => !!id);

/**
 * Las hermandades del día con sus detalles, tolerando el formato antiguo
 * (`hermandades` como array de ids sueltos). Mismo criterio que usa el libro.
 */
export const detallesDelDia = (dia: DiaSS | null | undefined): DiaSSHermandadDetalles[] => {
  if (!dia || !Array.isArray(dia.hermandades)) return [];
  return (dia.hermandades as (string | DiaSSHermandadDetalles)[])
    .map((h) => (typeof h === 'string' ? ({ hermandadId: h } as DiaSSHermandadDetalles) : h))
    .filter((h): h is DiaSSHermandadDetalles => !!h && !!h.hermandadId);
};

// ─── Horas de un cuadro de horarios ──────────────────────────────────────────

/**
 * A partir de esta hora se entiende que la hora pertenece YA al día siguiente:
 * una cofradía que se recoge a las 02:30 va DESPUÉS de la que pasa a las 23:50,
 * no antes. El corte está en las 07:00 porque no hay procesión que salga de
 * madrugada y llegue hasta ahí (la Madrugá sale a las 00:00 y se recoge por la
 * mañana, así que todo lo suyo cuenta como día siguiente).
 */
export const HORA_CORTE_MADRUGADA = 7 * 60;

const minutosDelDia = (hora: string): number => {
  const [h, m] = (hora || '').split(':').map((n) => parseInt(n, 10));
  if (isNaN(h) || isNaN(m)) return 0;
  return h * 60 + m;
};

/** ¿Esta hora es de madrugada del día siguiente? (00:00–06:59) */
export const esMadrugada = (hora: string): boolean => minutosDelDia(hora) < HORA_CORTE_MADRUGADA;

/** Clave de orden de una hora del cuadro: la madrugada se va al final. */
export const ordenHorario = (hora: string): number => {
  const m = minutosDelDia(hora);
  return m < HORA_CORTE_MADRUGADA ? m + 1440 : m;
};

/**
 * Horas ordenadas de la tarde a la madrugada. Es el orden que vale cuando el
 * eje de horas lo comparten VARIAS hermandades (el cuadro del libro), donde no
 * hay una única hora de salida a la que anclarse.
 */
export const ordenarHoras = (horas: string[]): string[] =>
  [...horas].sort((a, b) => ordenHorario(a) - ordenHorario(b));

/** La hora marcada con un tipo concreto dentro de un cuadro de horarios, si la hay. */
export const horaDeTipo = (
  itinerario: Record<string, { tipo?: string }> | null | undefined,
  tipo: string,
): string | null => {
  const entrada = Object.entries(itinerario || {}).find(([, v]) => v?.tipo === tipo);
  return entrada ? entrada[0] : null;
};

/** La hora marcada como salida dentro de un cuadro de horarios, si la hay. */
export const horaDeSalida = (itinerario?: Record<string, { tipo?: string }> | null): string | null =>
  horaDeTipo(itinerario, 'salida');

// ─── Orden de las columnas del cuadro de horarios ────────────────────────────

/**
 * Clave de orden de una hermandad dentro del cuadro de horarios del día.
 *
 * En **Semana Santa** manda la hora de **Carrera Oficial** (es el eje que
 * comparten todas: el cuadro se lee para saber quién entra antes) y en
 * **vísperas**, que no la recorren, la **hora de salida**. Si a una hermandad le
 * falta su hora de referencia se cae a la de salida, y si tampoco la tiene se va
 * al final: sin dato no hay forma de colocarla y es mejor que no descoloque a
 * las demás.
 *
 * Se usa `ordenHorario`, o sea el corte de las 07:00: una que pase por la
 * Carrera Oficial a la 01:30 va DESPUÉS de la que pasa a las 23:00, no antes.
 */
export function claveColumnaCuadro(
  itinerario: Record<string, { tipo?: string }> | null | undefined,
  esVispera: boolean,
): number {
  const principal = horaDeTipo(itinerario, esVispera ? 'salida' : 'carreraOficial');
  const hora = principal || horaDeSalida(itinerario);
  return hora ? ordenHorario(hora) : Number.POSITIVE_INFINITY;
}

/**
 * Las hermandades del día ordenadas para el cuadro de horarios: la que pasa
 * antes por la Carrera Oficial (o la que sale antes, en vísperas) a la
 * IZQUIERDA, y de ahí hacia la derecha.
 *
 * Empate → se conserva el orden que traían (el del cortejo del día), así que dos
 * hermandades con la misma hora no bailan de sitio entre renders.
 */
export function ordenarColumnasCuadro<T extends { itinerario?: Record<string, { tipo?: string }> }>(
  detalles: T[],
  esVispera: boolean,
): T[] {
  return detalles
    .map((d, i) => ({ d, i, k: claveColumnaCuadro(d.itinerario, esVispera) }))
    .sort((a, b) => (a.k === b.k ? a.i - b.i : a.k - b.k))
    .map((x) => x.d);
}

/**
 * Horas de UN cuadro concreto, ordenadas tomando su salida como principio.
 * Es más fino que `ordenarHoras`: la Madrugá sale a las 00:00 y se recoge a las
 * 08:00, y ahí la regla de las 07:00 se equivocaría de sentido.
 */
export const ordenarHorasDesde = (horas: string[], salida?: string | null): string[] => {
  if (!salida) return ordenarHoras(horas);
  const base = minutosDelDia(salida);
  const clave = (h: string) => (minutosDelDia(h) - base + 1440) % 1440;
  return [...horas].sort((a, b) => clave(a) - clave(b));
};

/** ¿Esta hora cae ya en el día siguiente al de la salida? */
export const esDiaSiguiente = (hora: string, salida?: string | null): boolean => {
  if (salida) return minutosDelDia(hora) < minutosDelDia(salida);
  return esMadrugada(hora);
};

// ─── Distancia de un recorrido ───────────────────────────────────────────────
// El mapa del programa es el mismo del servidor, y de este conocemos la
// calibración píxel↔coordenada de Minecraft. Como 1 bloque = 1 metro, se puede
// medir un recorrido de verdad: los puntos están en % de la imagen (0-100), así
// que basta con saber cuántos bloques mide un 1 % de ancho y de alto.

export interface EscalaMapa {
  /** Bloques (= metros) por cada punto porcentual del ancho de la imagen. */
  mPorPctX: number;
  /** Bloques (= metros) por cada punto porcentual del alto de la imagen. */
  mPorPctY: number;
}

/**
 * Escala del mapa a partir de sus dos puntos de calibración. Devuelve `null` si
 * el mapa no está calibrado o los dos puntos están en la misma vertical u
 * horizontal (no habría forma de deducir una de las dos escalas).
 */
export function escalaDelMapa(calib?: MapaCalibPunto[] | null): EscalaMapa | null {
  if (!Array.isArray(calib) || calib.length < 2) return null;
  const [a, b] = calib;
  const dPctX = b.xPct - a.xPct;
  const dPctY = b.yPct - a.yPct;
  if (!dPctX || !dPctY) return null;
  const mPorPctX = Math.abs((b.mcX - a.mcX) / dPctX);
  const mPorPctY = Math.abs((b.mcZ - a.mcZ) / dPctY);
  if (!isFinite(mPorPctX) || !isFinite(mPorPctY) || !mPorPctX || !mPorPctY) return null;
  return { mPorPctX, mPorPctY };
}

/** Longitud en metros de una polilínea de puntos en % de la imagen. */
export function longitudEnMetros(puntos: RecorridoPunto[] | undefined, escala: EscalaMapa | null): number {
  if (!escala || !Array.isArray(puntos) || puntos.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < puntos.length; i++) {
    const dx = (puntos[i].x - puntos[i - 1].x) * escala.mPorPctX;
    const dy = (puntos[i].y - puntos[i - 1].y) * escala.mPorPctY;
    total += Math.hypot(dx, dy);
  }
  return total;
}

/**
 * Metros que anda una cofradía: ida + vuelta y, en los días de Semana Santa,
 * la Carrera Oficial que va entre medias (las vísperas no la recorren).
 * Devuelve `null` si no hay forma de medirlo.
 */
export function metrosDelRecorrido(
  recorrido: RecorridoSolicitud | undefined,
  escala: EscalaMapa | null,
  metrosCarreraOficial = 0,
): number | null {
  if (!escala || !recorrido) return null;
  const ida = longitudEnMetros(recorrido.ida, escala);
  const vuelta = longitudEnMetros(recorrido.vuelta, escala);
  if (!ida && !vuelta) return null;
  return ida + vuelta + metrosCarreraOficial;
}

/** "2,43 km" — dos decimales, que es lo que distingue un recorrido de otro. */
export function formatKm(metros: number): string {
  return `${(metros / 1000).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
}
